import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Wallet, 
  Plus, 
  Send, 
  ArrowRight, 
  ExternalLink, 
  Copy, 
  Check, 
  RefreshCw, 
  Key, 
  Download, 
  Trash2, 
  Edit, 
  Eye, 
  EyeOff,
  AlertTriangle,
  Shield,
  Lock,
  Settings,
  ArrowUpRight,
  DollarSign,
  Coins,
  X,
  Activity,
  History,
  Power
} from 'lucide-react';
import { TradingWallet, getUserTradingWallets, getPrivateKeyBackup, deleteTradingWallet } from '../../services/tradingWalletService';
import { getBalance } from '../../services/shyftService';
import { getWalletTokensWithMetadata } from '../../services/solanatrackerService';
import CreateTradingWalletModal from '../../components/tradingWallet/CreateTradingWalletModal';
import InstantWalletTransfer from '../../components/tradingWallet/InstantWalletTransfer';
import { useWalletBalances } from '../../services/walletBalanceService';
import { hasWalletKeyPassword, verifyWalletKeyPassword, setWalletKeyPassword } from '../../services/userProfileService';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import { useWalletUser } from '../../hooks/useWalletUser';
import { notifyTradingWalletDeleted } from '../../components/WalletConnection';

// Constants
const LAMPORTS_PER_SOL = 1000000000;

interface TokenBalance {
  mint: string;
  symbol: string;
  balance: string;
  image?: string;
  value?: number;
  decimals?: number;
  name?: string;
}

interface WalletData extends TradingWallet {
  solBalance?: number;
  tokens?: TokenBalance[];
  totalValue?: number;
  lastUpdated?: number;
  isLoading?: boolean;
  hasError?: boolean;
}

interface WalletManagerProps {
  connectedWallet?: string;
  onShowConnectModal?: () => void;
  className?: string;
  onClose?: () => void;
  isWidget?: boolean;
  onAddWidget?: (widget: any) => void;
}

const WalletManager: React.FC<WalletManagerProps> = ({
  connectedWallet,
  onShowConnectModal,
  className = '',
  onClose,
  isWidget = false,
  onAddWidget
}) => {
  // Get wallet connection from hook
  const walletUser = useWalletUser();
  const effectiveWallet = connectedWallet || walletUser.walletAddress;
  
  // Check if we should show the connect wallet screen
  // Only show it if we're not loading and there's no effective wallet
  const shouldShowConnectWallet = !walletUser.isLoading && !effectiveWallet;
  
  // Core state
  const [wallets, setWallets] = useState<WalletData[]>([]);
  const [mainWalletBalance, setMainWalletBalance] = useState<number>(0);
  const [mainWalletTokens, setMainWalletTokens] = useState<TokenBalance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'tokens'>('overview');
  
  // Token filtering state
  const [selectedWalletFilter, setSelectedWalletFilter] = useState<string>('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferSourceWalletId, setTransferSourceWalletId] = useState<string | null>(null);
  
  // UI states
  const [selectedWallet, setSelectedWallet] = useState<WalletData | null>(null);
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Private key management states
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [privateKeyWallet, setPrivateKeyWallet] = useState<WalletData | null>(null);
  const [exportPassword, setExportPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [privateKeyBackup, setPrivateKeyBackup] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [passwordStep, setPasswordStep] = useState<'verify' | 'export'>('verify');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteWallet, setDeleteWallet] = useState<WalletData | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { refreshBalances } = useWalletBalances();

  // Utility functions
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (balance: number, decimals = 4) => {
    if (balance === 0) return '0';
    if (balance < 0.0001) return balance.toFixed(8);
    return balance.toFixed(decimals);
  };

  const formatUSD = (value: number) => {
    if (value === 0) return '$0.00';
    if (value < 0.01) return '<$0.01';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set([...prev, id]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  // Fetch wallet balances and token metadata with timeout handling
  const fetchWalletData = useCallback(async (wallet: TradingWallet): Promise<WalletData> => {
    const startTime = performance.now();
    console.log(`💰 Fetching data for ${wallet.public_key.slice(0, 8)}...`);
    
    try {
      // Add timeout promise to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
      });
      
      // Fetch SOL balance with timeout
      const balancePromise = getBalance(wallet.public_key);
      const lamports = await Promise.race([balancePromise, timeoutPromise]);
      const solBalance = lamports / LAMPORTS_PER_SOL;
      
      console.log(`💰 Got balance for ${wallet.public_key.slice(0, 8)} in ${(performance.now() - startTime).toFixed(2)}ms`);

      // Fetch token metadata with timeout (this is usually the slow part)
      const tokenStartTime = performance.now();
      const tokenPromise = getWalletTokensWithMetadata(wallet.public_key);
      const response = await Promise.race([tokenPromise, timeoutPromise]);
      
      console.log(`🪙 Got tokens for ${wallet.public_key.slice(0, 8)} in ${(performance.now() - tokenStartTime).toFixed(2)}ms`);
      
      let tokens: TokenBalance[] = [];
      let totalValue = solBalance * 200; // Approximate SOL price as fallback

      if (response.status === 'success' && response.data?.tokens) {
        tokens = response.data.tokens
          .filter((token: any) => parseFloat(token.balance) > 0)
          .map((token: any) => ({
            mint: token.mint,
            symbol: token.symbol || 'Unknown',
            name: token.name || token.symbol || 'Unknown',
            balance: token.balance,
            image: token.image,
            value: token.value || 0,
            decimals: token.decimals || 9
          }));

        // Calculate total value from API data
        totalValue = tokens.reduce((sum, token) => sum + (token.value || 0), 0);
      }

      const totalTime = performance.now() - startTime;
      console.log(`✅ Completed ${wallet.public_key.slice(0, 8)} in ${totalTime.toFixed(2)}ms`);

      return {
        ...wallet,
        solBalance,
        tokens,
        totalValue,
        lastUpdated: Date.now()
      };
    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error(`❌ Error fetching data for wallet ${wallet.public_key.slice(0, 8)} after ${totalTime.toFixed(2)}ms:`, error);
      return {
        ...wallet,
        solBalance: 0,
        tokens: [],
        totalValue: 0,
        lastUpdated: Date.now(),
        hasError: true
      };
    }
  }, []);

  // Fetch main wallet data with timeout handling
  const fetchMainWalletData = useCallback(async () => {
    if (!effectiveWallet) return;

    const startTime = performance.now();
    console.log(`💰 Fetching main wallet data for ${effectiveWallet.slice(0, 8)}...`);

    try {
      // Add timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Main wallet request timeout')), 10000);
      });

      // Fetch SOL balance
      const balancePromise = getBalance(effectiveWallet);
      const lamports = await Promise.race([balancePromise, timeoutPromise]);
      const solBalance = lamports / LAMPORTS_PER_SOL;
      setMainWalletBalance(solBalance);

      console.log(`💰 Got main wallet balance in ${(performance.now() - startTime).toFixed(2)}ms`);

      // Fetch token metadata
      const tokenStartTime = performance.now();
      const tokenPromise = getWalletTokensWithMetadata(effectiveWallet);
      const response = await Promise.race([tokenPromise, timeoutPromise]);
      
      console.log(`🪙 Got main wallet tokens in ${(performance.now() - tokenStartTime).toFixed(2)}ms`);
      
      if (response.status === 'success' && response.data?.tokens) {
        const tokens = response.data.tokens
          .filter((token: any) => parseFloat(token.balance) > 0)
          .map((token: any) => ({
            mint: token.mint,
            symbol: token.symbol || 'Unknown',
            name: token.name || token.symbol || 'Unknown',
            balance: token.balance,
            image: token.image,
            value: token.value || 0,
            decimals: token.decimals || 9
          }));
        
        setMainWalletTokens(tokens);
      }

      const totalTime = performance.now() - startTime;
      console.log(`✅ Completed main wallet in ${totalTime.toFixed(2)}ms`);
    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error(`❌ Error fetching main wallet data after ${totalTime.toFixed(2)}ms:`, error);
      // Set fallback values on error
      setMainWalletBalance(0);
      setMainWalletTokens([]);
    }
  }, [effectiveWallet]);

  // Load all wallets with progressive loading for better performance
  const loadWallets = useCallback(async () => {
    if (!effectiveWallet) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log('📋 Loading trading wallets...');
      const startTime = performance.now();
      
      // Step 1: Get wallet list immediately (fast)
      const tradingWallets = await getUserTradingWallets(effectiveWallet);
      console.log(`📋 Got ${tradingWallets.length} wallets in ${(performance.now() - startTime).toFixed(2)}ms`);
      
      // Step 2: Show wallets immediately with basic info
      const walletsWithBasicData = tradingWallets.map(wallet => ({
        ...wallet,
        solBalance: 0,
        tokens: [],
        totalValue: 0,
        lastUpdated: Date.now(),
        isLoading: true // Add loading flag
      }));
      
      setWallets(walletsWithBasicData);
      setIsLoading(false); // Show UI immediately
      
      // Step 3: Load balance data progressively in background
      console.log('💰 Loading wallet balances progressively...');
      
      // Load main wallet balance first (most important)
      fetchMainWalletData().catch(err => 
        console.error('Error loading main wallet:', err)
      );
      
      // Then load trading wallet balances progressively
      tradingWallets.forEach(async (wallet, index) => {
        try {
          // Add small delay between requests to avoid overwhelming APIs
          if (index > 0) {
            await new Promise(resolve => setTimeout(resolve, 200 * index));
          }
          
          console.log(`💰 Loading data for wallet ${index + 1}/${tradingWallets.length}: ${wallet.public_key.slice(0, 8)}...`);
          const walletData = await fetchWalletData(wallet);
          
          // Update specific wallet when data loads
          setWallets(prev => prev.map(w => 
            w.public_key === wallet.public_key 
              ? { ...walletData, isLoading: false }
              : w
          ));
          
        } catch (error) {
          console.error(`❌ Error loading wallet ${wallet.public_key.slice(0, 8)}:`, error);
          // Update wallet to show error state
          setWallets(prev => prev.map(w => 
            w.public_key === wallet.public_key 
              ? { ...w, isLoading: false, hasError: true }
              : w
          ));
        }
      });
      
    } catch (err) {
      console.error('❌ Error loading wallets:', err);
      setError('Failed to load wallets');
      setIsLoading(false);
    }
  }, [effectiveWallet, fetchWalletData, fetchMainWalletData]);

  // Load wallets on mount and when connected wallet changes
  useEffect(() => {
    if (effectiveWallet) {
      loadWallets();
    }
  }, [effectiveWallet, loadWallets, refreshTrigger]);

  // Handle wallet creation
  const handleWalletCreated = () => {
    setShowCreateModal(false);
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle transfer success
  const handleTransferSuccess = () => {
    setShowTransferModal(false);
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle private key export
  const handleExportPrivateKey = async (wallet: WalletData) => {
    setPrivateKeyWallet(wallet);
    setShowPrivateKeyModal(true);
    setPasswordStep('verify');
    setVerifyPassword('');
    setExportPassword('');
    setConfirmPassword('');
    setPrivateKeyBackup('');
    
    // Check if user has password protection enabled
    try {
      const hasPass = await hasWalletKeyPassword(effectiveWallet!);
      setHasPassword(hasPass);
    } catch (error) {
      console.error('Error checking password status:', error);
    }
  };

  const handlePasswordVerification = async () => {
    if (!privateKeyWallet || !effectiveWallet) return;

    setIsVerifying(true);
    try {
      if (hasPassword) {
        // Verify existing password
        const isValid = await verifyWalletKeyPassword(effectiveWallet, verifyPassword);
        if (!isValid) {
          setError('Invalid password');
          setIsVerifying(false);
          return;
        }
      }
      
      // Password verified or no password set, proceed to export step
      setPasswordStep('export');
      setError(null);
    } catch (error) {
      console.error('Error verifying password:', error);
      setError('Failed to verify password');
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePrivateKeyExport = async () => {
    if (!privateKeyWallet || !effectiveWallet) return;

    setIsExporting(true);
    try {
      const passwordToUse = hasPassword ? verifyPassword : exportPassword;
      
      if (!hasPassword && exportPassword) {
        // Set up password protection
        await setWalletKeyPassword(effectiveWallet, exportPassword);
      }

      // Password is only for access control, always get raw backup format
      const backup = await getPrivateKeyBackup(
        effectiveWallet,
        privateKeyWallet.public_key
      );

      setPrivateKeyBackup(backup);
    } catch (error) {
      console.error('Error exporting private key:', error);
      setError('Failed to export private key');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle wallet deletion
  const handleDeleteWallet = async (wallet: WalletData) => {
    setDeleteWallet(wallet);
    setShowDeleteModal(true);
  };

  const confirmDeleteWallet = async () => {
    if (!deleteWallet || !effectiveWallet || deleteConfirmation !== 'DELETE') return;

    setIsDeleting(true);
    try {
      await deleteTradingWallet(effectiveWallet, deleteWallet.public_key);
      setWallets(prev => prev.filter(w => w.public_key !== deleteWallet.public_key));
      setShowDeleteModal(false);
      setDeleteWallet(null);
      setDeleteConfirmation('');
      
      // Notify WalletConnection component to refresh the trading wallets list
      notifyTradingWalletDeleted();
    } catch (error) {
      console.error('Error deleting wallet:', error);
      setError('Failed to delete wallet');
    } finally {
      setIsDeleting(false);
    }
  };

  // Token Item Component
  const TokenItem: React.FC<{
    token: TokenBalance;
    className?: string;
  }> = ({ token, className = '' }) => (
    <div className={`flex items-center gap-2 py-2 px-3 ${className}`}>
      {token.image && (
        <img 
          src={token.image} 
          alt={token.symbol} 
          className="w-6 h-6 rounded-full flex-shrink-0"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-white truncate">{token.symbol}</span>
          {token.name && token.name !== token.symbol && (
            <span className="text-xs text-gray-400 truncate">({token.name})</span>
          )}
        </div>
        <div className="text-xs text-gray-400 font-mono">
          {formatBalance(parseFloat(token.balance), 4)}
        </div>
      </div>
      {token.value && token.value > 0 && (
        <div className="text-xs text-[#24c35b] font-medium">
          {formatUSD(token.value)}
        </div>
      )}
    </div>
  );

  // Wallet Card Component
  const WalletCard: React.FC<{
    wallet: WalletData;
    isMain?: boolean;
  }> = ({ wallet, isMain = false }) => (
    <div className="p-3 rounded-lg bg-white border border-gray-200 hover:border-[#24c35b]/30 transition-colors shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            isMain ? 'bg-blue-500/20' : 'bg-[#24c35b]/20'
          }`}>
            {isMain ? (
              <Shield className="w-3 h-3 text-blue-400" />
            ) : (
              <Wallet className="w-3 h-3 text-[#24c35b]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-black font-medium text-sm truncate">
                {isMain ? 'Main Wallet' : wallet.name}
              </span>
              <button
                onClick={() => copyToClipboard(
                  isMain ? effectiveWallet! : wallet.public_key, 
                  isMain ? 'main-wallet' : wallet.public_key
                )}
                className="text-gray-500 hover:text-black transition-colors"
              >
                {copiedItems.has(isMain ? 'main-wallet' : wallet.public_key) ? (
                  <Check className="w-3 h-3 text-[#24c35b]" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            </div>
            <p className="text-gray-500 text-xs font-mono">
              {formatAddress(isMain ? effectiveWallet! : wallet.public_key)}
            </p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm font-bold text-black flex items-center gap-1">
            {!isMain && wallet.isLoading ? (
              <>
                <RefreshCw size={12} className="animate-spin text-gray-400" />
                <span className="text-gray-500">Loading...</span>
              </>
            ) : wallet.hasError ? (
              <span className="text-red-500 text-xs">Error</span>
            ) : (
              <>
                {formatBalance(isMain ? mainWalletBalance : (wallet.solBalance || 0))} SOL
              </>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {!isMain && wallet.isLoading ? (
              <span className="text-gray-500">...</span>
            ) : (
              <>
                {isMain ? mainWalletTokens.length : (wallet.tokens?.length || 0)} tokens
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            setTransferSourceWalletId(isMain ? 'main' : wallet.public_key);
            setShowTransferModal(true);
          }}
          className="flex-1 py-2 px-3 rounded-lg bg-[#24c35b] text-white text-sm hover:bg-[#20a855] transition-all flex items-center justify-center gap-1 shadow-sm"
        >
          <Send className="w-3 h-3" />
          Transfer
        </button>
        {!isMain && (
          <>
            <button
              onClick={() => handleExportPrivateKey(wallet)}
              className="py-2 px-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 text-sm hover:bg-yellow-500/20 transition-all"
            >
              <Key className="w-3 h-3" />
            </button>
            <button
              onClick={() => handleDeleteWallet(wallet)}
              className="py-2 px-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 text-sm hover:bg-red-500/20 transition-all"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  // Render Overview Tab
  const renderOverview = () => (
    <div className="space-y-3">
      {/* Main Wallet */}
      <WalletCard wallet={{} as WalletData} isMain={true} />
      
      {/* Trading Wallets */}
      {wallets.length > 0 ? (
        wallets.map((wallet) => (
          <WalletCard key={wallet.public_key} wallet={wallet} />
        ))
      ) : (
        <div className="p-6 rounded-lg bg-white border border-gray-200 text-center shadow-sm">
          <div className="p-3 rounded-full bg-[#24c35b]/10 text-[#24c35b] mx-auto w-fit mb-3">
            <Wallet className="w-5 h-5" />
          </div>
          <p className="text-gray-600 mb-4 text-sm">No trading wallets created yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 rounded-lg bg-[#24c35b] text-white text-sm hover:bg-[#20a855] transition-all shadow-sm"
          >
            Create Your First Trading Wallet
          </button>
        </div>
      )}
    </div>
  );

  // Render Tokens Tab
  const renderTokens = () => {
    const allTokens = [
      ...mainWalletTokens.map(token => ({ ...token, walletType: 'Main Wallet', walletId: 'main' })),
      ...wallets.flatMap(wallet => 
        (wallet.tokens || []).map(token => ({ ...token, walletType: wallet.name, walletId: wallet.public_key }))
      )
    ];

    // Filter tokens based on selected wallet
    const filteredTokens = selectedWalletFilter === 'all' 
      ? allTokens 
      : allTokens.filter(token => token.walletId === selectedWalletFilter);

    // Get wallet options for filter
    const walletOptions = [
      { id: 'all', name: 'All Wallets' },
      { id: 'main', name: 'Main Wallet' },
      ...wallets.map(wallet => ({ id: wallet.public_key, name: wallet.name }))
    ];

    return (
      <div className="space-y-2">
        {/* Wallet Filter */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-400">Filter by wallet:</span>
          <select
            value={selectedWalletFilter}
            onChange={(e) => setSelectedWalletFilter(e.target.value)}
            className="flex-1 flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-black text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 focus:outline-none transition-all"
          >
            {walletOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        {filteredTokens.length > 0 ? (
          <div className="space-y-1">
            {filteredTokens.map((token, index) => (
              <div key={`${token.mint}-${index}`} className="rounded bg-white border border-gray-200 shadow-sm">
                <TokenItem token={token} />
                <div className="px-3 pb-2">
                  <span className="text-xs text-gray-500">in {token.walletType}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Coins size={24} className="text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 text-sm">
              {selectedWalletFilter === 'all' ? 'No tokens found' : 'No tokens in selected wallet'}
            </p>
            <p className="text-gray-500 text-xs mt-1">
              {selectedWalletFilter === 'all' 
                ? 'Tokens will appear here when you have balances' 
                : 'Try selecting a different wallet or add tokens to this wallet'
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  // Show loading state while wallet connection is being detected
  if (walletUser.isLoading) {
    return (
      <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`}>
        <style>{globalWidgetTextStyles}</style>
        
        {/* Header */}
        <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
            <Wallet size={18} className="text-[#24c35b]" />
            <h2 className="text-lg font-bold text-black">Wallet Manager</h2>
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex-grow overflow-y-auto px-4 py-6 bg-white">
          <div className="text-center py-8">
            <div className="p-4 rounded-full bg-[#24c35b]/10 text-[#24c35b] mx-auto w-fit mb-4">
              <RefreshCw size={24} className="animate-spin" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-black">Loading Wallet Data</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Initializing wallet manager...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (shouldShowConnectWallet) {
    return (
      <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`}>
        <style>{globalWidgetTextStyles}</style>
        
        {/* Header */}
        <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
            <Wallet size={18} className="text-[#24c35b]" />
            <h2 className="text-lg font-bold text-black">Wallet Manager</h2>
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex-grow overflow-y-auto px-4 py-6 bg-white">
          <div className="text-center py-8">
            <div className="p-4 rounded-full bg-[#24c35b]/10 text-[#24c35b] mx-auto w-fit mb-4">
              <Lock size={24} />
            </div>
            <h3 className="text-xl font-bold mb-3 text-black">Connect Your Wallet</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Connect your wallet to manage trading wallets and transfers
            </p>
            <button
              onClick={() => {
                if (onShowConnectModal) {
                  onShowConnectModal();
                } else {
                  // Dispatch global event for wallet connection
                  window.dispatchEvent(new CustomEvent('ekko:show_wallet_modal'));
                }
              }}
              className="px-4 py-2 rounded-lg bg-[#24c35b] text-white hover:bg-[#20a855] transition-all shadow-sm"
            >
              Connect Wallet
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main content for widget
  const content = (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`}>
      <style>{globalWidgetTextStyles}</style>
      <style>{`
        /* Custom scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(36, 195, 91, 0.3);
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(36, 195, 91, 0.5);
        }
      `}</style>
      
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <Wallet size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Wallet Manager</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
            disabled={isLoading}
            title="Refresh"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-3 py-1.5 rounded-lg bg-[#24c35b] text-white text-sm hover:bg-[#20a855] transition-all flex items-center gap-1 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
              title="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {[
          { key: 'overview', label: 'Overview', icon: Activity },
          { key: 'tokens', label: 'Tokens', icon: Coins }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as any)}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-all border-b-2 ${
              activeTab === key
                ? 'border-[#24c35b] text-[#24c35b] bg-white'
                : 'border-transparent text-gray-600 hover:text-black hover:bg-gray-100'
            }`}
          >
            <Icon size={16} className="mr-2 inline" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto px-4 py-3 custom-scrollbar" style={{ background: '#ffffff', borderRadius: 0, overflowX: 'visible' }}>
        {/* Error Display */}
        {error && (
          <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200">
            <div className="text-red-600 text-sm font-medium mb-1">Error</div>
            <div className="text-red-500 text-xs">{error}</div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <RefreshCw size={16} className="text-[#24c35b] animate-spin mr-2" />
            <span className="text-gray-600 text-xs">Loading wallets...</span>
          </div>
        )}

        {/* Tab Content */}
        {!isLoading && (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'tokens' && renderTokens()}
          </>
        )}
      </div>

      {/* Create Wallet Modal */}
      <CreateTradingWalletModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onWalletCreated={handleWalletCreated}
        connectedWalletAddress={effectiveWallet}
      />

      {/* Transfer Modal */}
      {showTransferModal && transferSourceWalletId && (
        <InstantWalletTransfer
          wallets={wallets}
          userWalletAddress={effectiveWallet}
          sourceWalletId={transferSourceWalletId}
          onClose={() => setShowTransferModal(false)}
          onSuccess={handleTransferSuccess}
          onWalletUpdate={(updatedWallet) => {
            setWallets(prev =>
              prev.map(w => w.public_key === updatedWallet.public_key ? { ...w, ...updatedWallet } : w)
            );
          }}
        />
      )}

      {/* Private Key Export Modal */}
      {showPrivateKeyModal && privateKeyWallet && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-black text-lg font-semibold flex items-center">
                <Key size={20} className="text-[#24c35b] mr-2" />
                Export Private Key
              </h3>
              <button
                onClick={() => {
                  setShowPrivateKeyModal(false);
                  setPrivateKeyWallet(null);
                  setExportPassword('');
                  setConfirmPassword('');
                  setVerifyPassword('');
                  setPrivateKeyBackup('');
                  setPasswordStep('verify');
                }}
                className="text-gray-600 hover:text-black transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4 p-3 rounded border border-[#24c35b]/40 bg-[#24c35b]/10">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#24c35b] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[#24c35b] font-medium text-sm">Security Warning</p>
                    <p className="text-gray-700 text-xs">
                      Keep your private key secure. Anyone with access can control your wallet.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 text-sm mb-1">
                  Wallet: <span className="text-black font-medium">{privateKeyWallet.name}</span>
                </p>
                <p className="text-gray-600 text-sm">
                  Address: <span className="text-black font-medium">{formatAddress(privateKeyWallet.public_key)}</span>
                </p>
              </div>

              {!privateKeyBackup ? (
                <>
                  {passwordStep === 'verify' ? (
                    <div className="space-y-4">
                      {hasPassword ? (
                        <div>
                          <label className="block text-sm font-medium text-black mb-2">
                            Enter your wallet password
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={verifyPassword}
                              onChange={(e) => setVerifyPassword(e.target.value)}
                              className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                              placeholder="Enter your password"
                              onKeyPress={(e) => e.key === 'Enter' && handlePasswordVerification()}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-gray-600 text-sm mb-2">
                            No password protection is currently set up.
                          </p>
                          <p className="text-gray-500 text-xs">
                            You can optionally set a password in the next step.
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowPrivateKeyModal(false);
                            setPrivateKeyWallet(null);
                            setVerifyPassword('');
                            setPasswordStep('verify');
                          }}
                          className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={hasPassword ? handlePasswordVerification : () => setPasswordStep('export')}
                          disabled={isVerifying || (hasPassword && !verifyPassword)}
                          className="flex-1 py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isVerifying ? 'Verifying...' : 'Continue'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {!hasPassword && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-black mb-2">
                              Export Password (Optional)
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={exportPassword}
                                onChange={(e) => setExportPassword(e.target.value)}
                                className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                                placeholder="Enter password to encrypt backup"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          {exportPassword && (
                            <div>
                              <label className="block text-sm font-medium text-black mb-2">
                                Confirm Password
                              </label>
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                                placeholder="Confirm password"
                              />
                            </div>
                          )}
                        </>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => setPasswordStep('verify')}
                          className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
                        >
                          Back
                        </button>
                        <button
                          onClick={handlePrivateKeyExport}
                          disabled={isExporting || (exportPassword !== '' && exportPassword !== confirmPassword)}
                          className="flex-1 py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isExporting ? 'Exporting...' : 'Export'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Private Key Backup
                    </label>
                    <div className="relative">
                      <textarea
                        value={privateKeyBackup}
                        readOnly
                        className="w-full h-32 py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black font-mono text-xs resize-none"
                      />
                      <button
                        onClick={() => copyToClipboard(privateKeyBackup, 'private-key')}
                        className="absolute top-2 right-2 p-1 rounded bg-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/30 transition-colors"
                      >
                        {copiedItems.has('private-key') ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      const blob = new Blob([privateKeyBackup], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${privateKeyWallet.name}-private-key.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    className="w-full py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Backup File
                  </button>

                  <button
                    onClick={() => {
                      setShowPrivateKeyModal(false);
                      setPrivateKeyWallet(null);
                      setExportPassword('');
                      setConfirmPassword('');
                      setVerifyPassword('');
                      setPrivateKeyBackup('');
                      setPasswordStep('verify');
                    }}
                    className="w-full py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteWallet && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-black text-lg font-semibold flex items-center">
                <Trash2 size={20} className="text-red-500 mr-2" />
                Delete Wallet
              </h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteWallet(null);
                  setDeleteConfirmation('');
                }}
                className="text-gray-600 hover:text-black transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-500 font-medium text-sm">Permanent Action</p>
                    <p className="text-gray-700 text-xs">
                      This will permanently delete the wallet. Make sure you have backed up your private key.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 text-sm mb-1">
                  Wallet: <span className="text-black font-medium">{deleteWallet.name}</span>
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  Balance: <span className="text-black font-medium">{formatBalance(deleteWallet.solBalance || 0)} SOL</span>
                </p>

                <label className="block text-sm font-medium text-black mb-2">
                  Type "DELETE" to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                  placeholder="DELETE"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteWallet(null);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteWallet}
                  disabled={isDeleting || deleteConfirmation !== 'DELETE'}
                  className="flex-1 py-2 px-4 rounded bg-red-500/20 border border-red-500/40 text-red-500 hover:bg-red-500/30 transition-all text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : (
                    <>
                      <Trash2 size={14} className="mr-1" />
                      Delete Wallet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">{content}</div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-white border border-gray-200/50 rounded-lg w-full max-w-[420px] shadow-xl ${widgetClassName}`}>
        {content}
      </div>
    </div>
  );
};

// Create widget function for canvas integration
export function createWalletManagerWidget(onAddWidget?: (widget: any) => void) {
  return {
    id: `wallet-manager-${Date.now()}`,
    type: 'wallet-manager',
    title: 'Wallet Manager',
    content: <WalletManager isWidget={true} onAddWidget={onAddWidget} />,
    size: { width: 400, height: 600 },
    defaultSize: { width: 400, height: 600 },
    minSize: { width: 350, height: 500 },
    maxSize: { width: 500, height: 800 },
    resizable: true,
    contentStyle: { 
      padding: 0,
      overflow: "hidden",
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "12px",
      border: "1px solid rgba(36, 195, 91, 0.2)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
      overflow: "hidden",
    },
    props: {
      isWidget: true
    }
  };
}

export default WalletManager; 